import { PointCloudObject } from "@/object-cache";
import {
  LodPointCloudRendererBase,
  LodPointCloudRendererBaseProps,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { useRef, useState, type JSX } from "react";
import { Group, Matrix4 } from "three";

export type PositionedPointCloudProps = Omit<
  LodPointCloudRendererBaseProps,
  "pointCloud"
> & {
  /** The point cloud to render with the correct position. */
  pointCloudObject: PointCloudObject;

  /** The global position to apply to the point cloud. */
  transform: Matrix4;

  /** Callback to execute when the initial position of the point cloud is applied. */
  onInitialPositioning?(): void;

  /** Whether the point cloud should be visible. */
  isVisible?: boolean;
};

/** @returns The rendered point cloud, positioned according to the world position and the transform overrides. */
export function PositionedPointCloud({
  pointCloudObject,
  transform,
  onInitialPositioning,
  isVisible,
  ...rest
}: PositionedPointCloudProps): JSX.Element | null {
  const [groupRef, setGroupRef] = useState<Group | null>(null);
  const isInitialPosition = useRef(true);

  // Apply the transform to the group
  useNonExhaustiveEffect(() => {
    if (groupRef) {
      applyTransformToGroup(groupRef, transform);

      if (isInitialPosition.current) {
        isInitialPosition.current = false;
        onInitialPositioning?.();
      }
    }
  }, [groupRef, transform]);

  // this check is to make the pointcloud invisible with immediate effect on visibility toggle
  if (!isVisible) {
    return null;
  }

  return (
    <group name="positioned point cloud" ref={setGroupRef}>
      {
        // Don't render before the position can be applied
        groupRef && (
          <LodPointCloudRendererBase pointCloud={pointCloudObject} {...rest} />
        )
      }
    </group>
  );
}

/**
 * Apply the given transform to the group and update its world matrix.
 *
 * @param group A reference to the group to apply the transform on.
 * @param transform The new transform of the point cloud.
 */
export function applyTransformToGroup(group: Group, transform: Matrix4): void {
  transform.decompose(group.position, group.quaternion, group.scale);
  group.updateWorldMatrix(true, true);
}
